<template>
  <OnBoardingFrame
    back
    :step="7"
    :title="$t('on_boarding.profile_source_page.title')"
    :subtitle="$t('on_boarding.profile_source_page.subtitle')"
    :error="errorMessage"
    split-footer
    @back="backTapped"
  >
    <!-- Step 7; "Where are you coming from?" follows and saves everything.
         Mirrors the question iOS asks on its legacy email signup
         (EmailViewModel) and stores the answer in the same place,
         `users.attribution_tag`, using the same options so the two
         platforms' answers group together.

         Optional on purpose: it's a question for us, not for the user, so it must
         never stand between someone and the app. Skip sits next to Continue. -->
    <ul class="ob-chips sources" role="listbox" :aria-label="$t('on_boarding.profile_source_page.title')">
      <li v-for="source in sources" :key="source.value">
        <button
          type="button"
          class="ob-chip source"
          :class="{ 'ob-chip--on source--selected': source.value === selected }"
          role="option"
          :aria-selected="source.value === selected"
          @click="choose(source.value)"
        >
          <span v-if="source.value === selected" class="ms" aria-hidden="true">check</span>
          {{ source.label }}
        </button>
      </li>
    </ul>

    <template #footer>
      <button type="button" class="ob-btn ob-btn-secondary ob-skip" :disabled="isLoading" @click="skip">
        {{ $t('on_boarding.profile_source_page.skip') }}
      </button>
      <button type="button" class="ob-btn ob-btn-primary" :disabled="isLoading || !selected" @click="submit">
        {{ $t('on_boarding.profile_source_page.continue') }}
      </button>
    </template>
  </OnBoardingFrame>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingFrame from './OnBoardingFrame.vue';
import { ATTRIBUTION_SOURCES, type AttributionSource } from '../../../utils/helpers/signupProfile';

export default defineComponent({
  name: 'OnBoardingProfileSourcePage',
  components: { OnBoardingFrame },
  data() {
    return {
      selected: this.$store.state.onBoarding.profileAttributionTag as string | null,
    };
  },
  computed: {
    // Every label is named by a literal $t() call rather than a key built from
    // `source.labelKey`. The client bundle is assembled by scanning these call
    // sites (lib/i18n_client_bundle.rb), and a key stitched together at runtime
    // is invisible to that scan — the strings would simply be absent from the
    // bundle and the step would render raw key names.
    sources(): Array<AttributionSource & { label: string }> {
      const labels: Record<string, string> = {
        search_engine: this.$t('on_boarding.profile_source_page.search_engine'),
        chatgpt: this.$t('on_boarding.profile_source_page.chatgpt'),
        grok: this.$t('on_boarding.profile_source_page.grok'),
        muse: this.$t('on_boarding.profile_source_page.muse'),
        facebook_ad: this.$t('on_boarding.profile_source_page.facebook_ad'),
        instagram_ad: this.$t('on_boarding.profile_source_page.instagram_ad'),
        social_media: this.$t('on_boarding.profile_source_page.social_media'),
        word_of_mouth: this.$t('on_boarding.profile_source_page.word_of_mouth'),
        podcast: this.$t('on_boarding.profile_source_page.podcast'),
        youtube: this.$t('on_boarding.profile_source_page.youtube'),
        influencer: this.$t('on_boarding.profile_source_page.influencer'),
        other: this.$t('on_boarding.profile_source_page.other'),
      };
      return ATTRIBUTION_SOURCES.map((source) => ({ ...source, label: labels[source.labelKey] }));
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
  },
  methods: {
    // Tapping the chosen option again clears it — the only way back to "no
    // answer" once something has been picked.
    choose(value: string) {
      this.selected = this.selected === value ? null : value;
    },
    submit() {
      this.$store.dispatch('onBoarding/submitProfileSource', this.selected);
    },
    // An explicit null, so a picked answer is cleared rather than kept.
    skip() {
      this.selected = null;
      this.$store.dispatch('onBoarding/submitProfileSource', null);
    },
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>
